import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { QuoteCivilStatus, QuoteRequest } from '../model/quote-request.model';
import { buildBreakdown } from '../mapper/quote-breakdown.mapper';
import { PolicyHolderStateService } from '../services/form/policy-holder-state.service';
import { QuoteApiResponse, QuoteService } from '../services/quote.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, switchMap, tap, timer } from 'rxjs';
import { CivilStatus } from '../../../core/enum/civil-status.enum';

const STORAGE_KEY = 'family-shield-quote-state';
export type DependentPackage = 'SPOUSE_CHILD' | 'CHILD_ONLY' | 'PARENT_SIBLING';

@Injectable({
  providedIn: 'root',
})
export class QuoteStateService {
  private policyHolderState = inject(PolicyHolderStateService);

  private quoteService = inject(QuoteService);

  readonly loading = signal(false);

  readonly retrievingFromApi = signal(false);

  readonly error = signal<string | null>(null);

  private _apiQuote = signal<QuoteApiResponse | null>(null);
  readonly apiQuote = this._apiQuote.asReadonly();

  // This is where the fields in the quote are stored.
  private _request = signal<QuoteRequest>(this.loadInitialState());
  readonly request = this._request.asReadonly();

  readonly totalPremium = computed(() => {
    return this.apiQuote()?.familyShieldPremium.total ?? null;
  });

  private previousEligibilityKey: string | null = null;

  readonly needsWithChildren = computed(() =>
    this.requiresWithChildren(this._request().policyHolderCivilStatus),
  );

  readonly dependentPackage = computed(() => this.resolveDependentPackage(this._request()));

  readonly breakdown = computed(() => {
    const request = this._request();
    if (!request.policyHolderCivilStatus || !this.dependentPackage()) return [];

    const apiQuote = this.apiQuote();
    if (!apiQuote) return [];

    return buildBreakdown(request.policyHolderCivilStatus, request.withChildren, apiQuote);
  });

  readonly isValid = computed(() => this.canRequestQuote(this._request()));

  // Setters
  setCivilStatus(status: QuoteCivilStatus) {
    this.patch({ policyHolderCivilStatus: status, withChildren: null });
  }

  setWithChildren(withChildren: boolean) {
    this.patch({ withChildren });
  }

  setFamilyLevel(level: number) {
    this.patch({ familyUnit: level });
  }

  setPersonalLevel(level: number) {
    this.patch({ ofwUnit: level });
  }

  reset() {
    this._request.set(this.getEmptyState());
    sessionStorage.removeItem(STORAGE_KEY);
  }

  // Session storage
  constructor() {
    effect(() => {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this._request()));
    });

    // Reset dependents when civil status or dependent package changes.
    effect(() => {
      const request = this._request();
      const current = request.policyHolderCivilStatus;
      if (!current) return;

      const eligibilityKey = `${current}:${request.withChildren ?? 'UNKNOWN'}`;

      if (eligibilityKey !== this.previousEligibilityKey) {
        if (this.previousEligibilityKey !== null) {
          this.policyHolderState.resetDependents();
        }

        this.previousEligibilityKey = eligibilityKey;
      }
    });

    // coverts signal to observable
    // because we wanted to use debounce
    toObservable(this._request)
      .pipe(
        switchMap((request) => {
          this.error.set(null);

          if (!this.canRequestQuote(request)) {
            this._apiQuote.set(null);
            this.loading.set(false);
            this.retrievingFromApi.set(false);
            return EMPTY;
          }

          this.loading.set(true);
          this.retrievingFromApi.set(true);
          this._apiQuote.set(null);

          return timer(400).pipe(
            switchMap(() =>
              this.quoteService.quoteFamilyShield(request).pipe(
                tap((response) => this._apiQuote.set(response)),
                catchError((err) => {
                  console.error(err);
                  this.error.set('Unable to compute quote. Please try again.');
                  return EMPTY;
                }),
                finalize(() => {
                  this.loading.set(false);
                  this.retrievingFromApi.set(false);
                }),
              ),
            ),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // Utility functions
  private loadInitialState(): QuoteRequest {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return this.getEmptyState();

    try {
      return {
        ...this.getEmptyState(),
        ...JSON.parse(stored),
      };
    } catch {
      return this.getEmptyState();
    }
  }

  private patch(partial: Partial<QuoteRequest>) {
    this.error.set(null);
    this._request.update((current) => ({
      ...current,
      ...partial,
    }));
  }

  private getEmptyState(): QuoteRequest {
    return {
      policyHolderCivilStatus: null,
      withChildren: null,
      familyUnit: 2,
      ofwUnit: 0,
    };
  }

  private requiresWithChildren(status: QuoteCivilStatus | null) {
    return !!status && status !== CivilStatus.Married;
  }

  private canRequestQuote(request: QuoteRequest) {
    if (!request.policyHolderCivilStatus) return false;
    return !this.requiresWithChildren(request.policyHolderCivilStatus) || request.withChildren !== null;
  }

  private resolveDependentPackage(request: QuoteRequest): DependentPackage | null {
    if (!request.policyHolderCivilStatus) return null;

    if (request.policyHolderCivilStatus === CivilStatus.Married) {
      return 'SPOUSE_CHILD';
    }

    if (request.withChildren === null) {
      return null;
    }

    return request.withChildren ? 'CHILD_ONLY' : 'PARENT_SIBLING';
  }
}
